import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { formatCurrency } from '../../utils/currency';
import api from '../../utils/api';
import PageHeader from './components/PageHeader';
import './LoanIncreaseRequestPage.css';

const LoanIncreaseRequestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loan = location.state?.loan;
  const { language } = useAuth();
  const { t } = useTranslation(language);

  const [requestedAmount, setRequestedAmount] = useState('');
  const [requestedTermMonths, setRequestedTermMonths] = useState('');
  const [requestedPaymentDate, setRequestedPaymentDate] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!loan) {
    navigate(-1);
    return null;
  }

  const currentAmount = parseFloat(loan.amount);
  const currentTerm = parseInt(loan.termMonths);
  const currentPaymentDate = parseInt(loan.interestPaymentDate);

  // Parse amount from formatted string
  const parseFormattedAmount = (formattedValue) => {
    return formattedValue.replace(/,/g, '');
  };

  // Format number with comma separator
  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    const numValue = value.replace(/,/g, '');
    if (isNaN(numValue)) return value;
    return parseFloat(numValue).toLocaleString('en-US');
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(value)) {
      setRequestedAmount(value);
    }
  };

  const displayAmount = requestedAmount ? formatNumberWithCommas(requestedAmount) : '';
  const increaseAmount = requestedAmount ? parseFloat(requestedAmount) - currentAmount : 0;
  const termChange = requestedTermMonths ? parseInt(requestedTermMonths) - currentTerm : 0;
  const paymentDateChange = requestedPaymentDate ? parseInt(requestedPaymentDate) - currentPaymentDate : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check at least one change
    if (!requestedAmount && !requestedTermMonths && !requestedPaymentDate) {
      setError(t('errorAtLeastOneChange'));
      return;
    }

    // Validate amount change if provided
    if (requestedAmount) {
      const newAmount = parseFloat(requestedAmount);
      if (newAmount === currentAmount) {
        setError(t('errorAmountMustDiffer'));
        return;
      }
    }

    // Validate payment date range if provided
    if (requestedPaymentDate) {
      const newDate = parseInt(requestedPaymentDate);
      if (newDate < 1 || newDate > 28) {
        setError(t('errorPaymentDateRange'));
        return;
      }
    }

    setLoading(true);

    try {
      const payload = {
        loanId: loan.id,
        reason: reason.trim() || null
      };

      if (requestedAmount) {
        payload.requestedAmount = parseFloat(requestedAmount);
      }
      if (requestedTermMonths) {
        payload.requestedTermMonths = parseInt(requestedTermMonths);
      }
      if (requestedPaymentDate) {
        payload.requestedPaymentDate = parseInt(requestedPaymentDate);
      }

      await api.post('/ctv/loan-increase-requests', payload);

      // Navigate back with success message
      navigate(-1, { state: { successMessage: t('changeRequestSuccess') } });
    } catch (err) {
      console.error('Failed to create change request:', err);
      setError(err.response?.data?.message || t('errorCannotSubmit'));
    } finally {
      setLoading(false);
    }
  };

  const hasAnyChange = requestedAmount || requestedTermMonths || requestedPaymentDate;

  return (
    <div className="loan-increase-request-page">
      <PageHeader title={t('loanIncreaseRequestTitle')} />

      <div className="page-content">
        <form onSubmit={handleSubmit} className="request-form">
          {/* Loan Info Section */}
          <div className="form-section">
            <div className="info-row">
              <label>{t('loanCodeLabel')}:</label>
              <span className="info-value">{loan.code}</span>
            </div>

            <div className="info-row">
              <label>{t('customerLabel')}:</label>
              <span className="info-value">{loan.customerName}</span>
            </div>
          </div>

          <div className="help-text info">
            {t('changeMultipleHint')}
          </div>

          {/* Amount Change Section */}
          <div className="form-section">
            <h3 className="section-title">{t('changeTypeAmount')}</h3>
            <div className="info-row">
              <label>{t('currentAmountLabel')}:</label>
              <span className="info-value highlight">{formatCurrency(currentAmount)}</span>
            </div>

            <div className="form-group">
              <label htmlFor="requestedAmount">
                {t('newAmountLabel')}
              </label>
              <input
                type="text"
                id="requestedAmount"
                value={displayAmount}
                onChange={handleAmountChange}
                placeholder={t('newAmountPlaceholder')}
                disabled={loading}
              />
              {requestedAmount && increaseAmount !== 0 && (
                <div className={`help-text ${increaseAmount > 0 ? 'success' : 'warning'}`}>
                  {increaseAmount > 0 ? t('increaseLabel') : t('decreaseLabel')}: <strong>{formatCurrency(Math.abs(increaseAmount))}</strong>
                </div>
              )}
            </div>
          </div>

          {/* Term Change Section */}
          <div className="form-section">
            <h3 className="section-title">{t('changeTypeTerm')}</h3>
            <div className="info-row">
              <label>{t('currentTermLabel')}:</label>
              <span className="info-value highlight">{currentTerm} {t('termUnit')}</span>
            </div>

            <div className="form-group">
              <label htmlFor="requestedTermMonths">
                {t('newTermLabel')}
              </label>
              <input
                type="number"
                id="requestedTermMonths"
                value={requestedTermMonths}
                onChange={(e) => setRequestedTermMonths(e.target.value)}
                placeholder={t('newTermPlaceholder')}
                disabled={loading}
                min="1"
                step="1"
              />
              {requestedTermMonths && termChange !== 0 && (
                <div className={`help-text ${termChange > 0 ? 'success' : 'warning'}`}>
                  {t('termChange')}: <strong>{termChange > 0 ? '+' : ''}{termChange} {t('termUnit')}</strong>
                </div>
              )}
            </div>
          </div>

          {/* Payment Date Change Section */}
          <div className="form-section">
            <h3 className="section-title">{t('changeTypePaymentDate')}</h3>
            <div className="info-row">
              <label>{t('currentPaymentDateLabel')}:</label>
              <span className="info-value highlight">{t('dayLabel', { day: currentPaymentDate })}</span>
            </div>

            <div className="form-group">
              <label htmlFor="requestedPaymentDate">
                {t('newPaymentDateLabel')}
              </label>
              <input
                type="number"
                id="requestedPaymentDate"
                value={requestedPaymentDate}
                onChange={(e) => setRequestedPaymentDate(e.target.value)}
                placeholder={t('newPaymentDatePlaceholder')}
                disabled={loading}
                min="1"
                max="28"
                step="1"
              />
              {requestedPaymentDate && paymentDateChange !== 0 && (
                <div className={`help-text ${paymentDateChange > 0 ? 'success' : 'warning'}`}>
                  {t('paymentDateChange')}: <strong>{paymentDateChange > 0 ? '+' : ''}{paymentDateChange} {t('dayLabel', { day: '' })}</strong>
                </div>
              )}
            </div>
          </div>

          {/* Reason Section */}
          <div className="form-group">
            <label htmlFor="reason">{t('reasonLabel')}</label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('reasonPlaceholder')}
              rows="4"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Submit Buttons */}
          <div className="form-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !hasAnyChange}
            >
              {loading ? t('submitting') : t('submitRequest')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoanIncreaseRequestPage;
